import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { motion, AnimatePresence } from "framer-motion";

type Sticker = { src: string; alt: string };

const DEFAULT_STICKERS: Sticker[] = [
  { src: "/images/stickers/wild-bear.png",       alt: "grizzly bear" },
  { src: "/images/stickers/ocean-turtle-2.png",  alt: "sea turtle" },
  { src: "/images/stickers/wild-yellowstone.png", alt: "Yellowstone" },
  { src: "/images/stickers/ocean-jelly-1.png",   alt: "jellyfish" },
  { src: "/images/stickers/wild-deer.png",       alt: "deer" },
  { src: "/images/stickers/ocean-shark.png",     alt: "shark" },
  { src: "/images/stickers/wild-mountain.png",   alt: "mountain" },
  { src: "/images/stickers/ocean-jelly-2.png",   alt: "jellyfish" },
  { src: "/images/stickers/wild-smokey.png",     alt: "Protect Our Forests" },
  { src: "/images/stickers/ocean-turtle-1.png",  alt: "turtle" },
  { src: "/images/stickers/wild-tree.png",       alt: "pine" },
  { src: "/images/stickers/ocean-sea.png",       alt: "Sea" },
];

type Props = {
  stickers?: Sticker[];
  radius?: number;
  size?: number;
};

/**
 * Circular 3D rotating gallery — GSAP positioned items orbit a centered point,
 * counter-rotate to stay upright, scale on hover, ring drifts toward cursor.
 *
 * Click any item → opens a centered zoom modal (Framer Motion) showing the
 * full sticker. Click outside or press Esc to dismiss.
 */
export function CircularGallery({
  stickers = DEFAULT_STICKERS,
  radius = 320,
  size = 168,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const [zoom, setZoom] = useState<Sticker | null>(null);

  useEffect(() => {
    const items = itemsRef.current;
    const total = items.length;
    if (!items.length || !galleryRef.current) return;

    items.forEach((item, i) => {
      const angle = (i / total) * Math.PI * 2;
      gsap.set(item, {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        left: "50%",
        top: "50%",
        xPercent: -50,
        yPercent: -50,
      });
    });

    const offHandlers: Array<() => void> = [];
    items.forEach((item) => {
      const onEnter = () => {
        gsap.to(item, { scale: 1.22, duration: 0.4, ease: "power3.out" });
        const img = item.querySelector("img");
        if (img) gsap.to(img, { scale: 1.12, duration: 0.4 });
      };
      const onLeave = () => {
        gsap.to(item, { scale: 1, duration: 0.4, ease: "power3.out" });
        const img = item.querySelector("img");
        if (img) gsap.to(img, { scale: 1, duration: 0.4 });
      };
      item.addEventListener("mouseenter", onEnter);
      item.addEventListener("mouseleave", onLeave);
      offHandlers.push(() => {
        item.removeEventListener("mouseenter", onEnter);
        item.removeEventListener("mouseleave", onLeave);
      });
    });

    const ringTween = gsap.to(galleryRef.current, {
      rotate: 360, duration: 42, repeat: -1, ease: "none",
    });
    const itemTween = gsap.to(items, {
      rotate: -360, duration: 42, repeat: -1, ease: "none",
    });

    const onMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 40;
      const y = (e.clientY / window.innerHeight - 0.5) * 40;
      gsap.to(galleryRef.current, { x, y, duration: 1, ease: "power3.out" });
    };
    window.addEventListener("mousemove", onMove);

    return () => {
      ringTween.kill();
      itemTween.kill();
      window.removeEventListener("mousemove", onMove);
      offHandlers.forEach((fn) => fn());
    };
  }, [radius, stickers.length]);

  // Esc dismisses the zoom
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  return (
    <>
      <div
        ref={wrapRef}
        className="relative w-full"
        style={{ height: `${(radius + size) * 2}px` }}
      >
        <div
          ref={galleryRef}
          className="relative left-1/2 top-1/2 will-change-transform"
          style={{
            width: 0, height: 0, transform: "translate(-50%, -50%)",
          }}
        >
          {stickers.map((s, i) => (
            <div
              key={s.src}
              ref={(el) => { if (el) itemsRef.current[i] = el; }}
              role="button"
              tabIndex={0}
              onClick={() => setZoom(s)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setZoom(s)}
              className="absolute rounded-3xl overflow-hidden cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-teal-300/70"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.10)",
                boxShadow:
                  "0 10px 30px rgba(0,0,0,0.5), 0 0 24px rgba(64,176,194,0.18), inset 0 0 0 1px rgba(255,255,255,0.04)",
                background:
                  "linear-gradient(180deg, rgba(64,176,194,0.10), rgba(255,255,255,0.02))",
                willChange: "transform",
              }}
            >
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "saturate(1.05) contrast(1.05)", willChange: "transform" }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Click-to-zoom modal */}
      <AnimatePresence>
        {zoom && (
          <>
            <motion.div
              key="zoom-overlay"
              className="fixed inset-0 bg-ink-1000/85 backdrop-blur-md cursor-zoom-out"
              style={{ zIndex: 9998 }}
              onClick={() => setZoom(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              key="zoom-content"
              className="fixed inset-0 grid place-items-center pointer-events-none"
              style={{ zIndex: 9999 }}
            >
              <motion.div
                initial={{ scale: 0.4, opacity: 0, rotate: -8 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.4, opacity: 0, rotate: 8 }}
                transition={{ type: "spring", stiffness: 220, damping: 24 }}
                className="pointer-events-auto rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_40px_120px_-30px_rgba(64,176,194,0.5)] cursor-zoom-out"
                style={{ width: "min(70vmin, 720px)", height: "min(70vmin, 720px)" }}
                onClick={() => setZoom(null)}
              >
                <img
                  src={zoom.src}
                  alt={zoom.alt}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim">
                {zoom.alt} · click anywhere to close
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
